import { useQuery } from "@apollo/client";
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import FooterDataDisclaimer from "components/company/FooterDataDisclaimer";
import { palette } from "lib/miolo";
import TotalYearlyAmericanIssuerCompensation from "lib/queries/TotalYearlyAmericanIssuerCompensation";
import type {
  ITotalYearlyAmericanIssuerCompensationQuery,
  ITotalYearlyAmericanIssuerCompensationQueryVariables,
} from "lib/queries/__generated__/TotalYearlyAmericanIssuerCompensation.generated";
import ClipLoader from "react-spinners/ClipLoader";
import AmericanTotalCompensations from "../content/AmericanTotalCompensations";
import ContentSectionBreadcrumbs from "../ContentSectionBreadcrumbs";
import AmericanIssuerTotalCompensationSummary from "../summary/AmericanIssuerTotalCompensationSummary";

interface IAmericanIssuerExecutiveCompensationCollapsibleProps {
  tickerSymbol: string;
  isExpanded: boolean;
  isREIT: boolean;
  onToggleCollapsed: () => void;
}

export default function AmericanIssuerExecutiveCompensationCollapsible({
  tickerSymbol,
  isExpanded,
  isREIT,
  onToggleCollapsed,
}: IAmericanIssuerExecutiveCompensationCollapsibleProps) {
  const { data, loading } = useQuery<
    ITotalYearlyAmericanIssuerCompensationQuery,
    ITotalYearlyAmericanIssuerCompensationQueryVariables
  >(TotalYearlyAmericanIssuerCompensation, {
    variables: {
      tickerSymbol,
    },
  });

  if (loading) {
    return <ClipLoader size={28} color={palette.royalBlue} loading={true} />;
  }

  if (data && data.totalYearlyAmericanIssuerCompensation?.length > 0) {
    return (
      <CollapsibleContentSection
        title="Executive Compensation"
        isExpanded={isExpanded}
        onToggleCollapsed={onToggleCollapsed}
        primaryColor={palette.mariner}
        renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Governance", "Executive Compensation"]} />}
        renderContentSummary={() => (
          <AmericanIssuerTotalCompensationSummary totalCompensations={data.totalYearlyAmericanIssuerCompensation} />
        )}
        renderContent={() => (
          <div className="space-y-4 font-aeonik font-normal">
            <div className="relative rounded-md border bg-white p-1">
              <AmericanTotalCompensations
                totalCompensations={data.totalYearlyAmericanIssuerCompensation}
                isREIT={isREIT}
              />
            </div>

            <FooterDataDisclaimer>
              The values above are in <strong>U.S. Dollar.</strong> Net Income and Revenue are reported in{" "}
              <strong>millions</strong>.
            </FooterDataDisclaimer>
          </div>
        )}
      />
    );
  }

  return null;
}
